import React from 'react';
import { Box, Typography } from '@mui/material';
import type { FaqSectionTemplate } from './types';
import { getFaqSectionTemplateLabel } from './faqSectionUtils';

interface FaqSectionTemplatePreviewProps {
  value: FaqSectionTemplate;
  onChange: (template: FaqSectionTemplate) => void;
}

const TEMPLATES: FaqSectionTemplate[] = ['accordion', 'tabs', 'contact-split'];

const FaqSectionTemplatePreview: React.FC<FaqSectionTemplatePreviewProps> = ({ value, onChange }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
    {TEMPLATES.map((template) => {
      const selected = value === template;
      return (
        <Box
          key={template}
          onClick={() => onChange(template)}
          sx={{
            cursor: 'pointer',
            borderRadius: 2,
            border: selected ? '2px solid' : '1px solid',
            borderColor: selected ? 'primary.main' : '#e2e8f0',
            bgcolor: selected ? 'primary.50' : '#fff',
            p: 1.5,
            transition: 'border-color 0.15s, background-color 0.15s'
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            {getFaqSectionTemplateLabel(template)}
          </Typography>

          {template === 'contact-split' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, minHeight: 92 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ width: '80%', height: 7, borderRadius: 1, bgcolor: '#1e293b' }} />
                <Box sx={{ width: '55%', height: 6, borderRadius: 1, bgcolor: '#c4a46a' }} />
                <Box sx={{ width: '90%', height: 4, borderRadius: 1, bgcolor: '#e7e5e4', mt: 0.5 }} />
                <Box sx={{ width: '70%', height: 4, borderRadius: 1, bgcolor: '#e7e5e4' }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ width: '75%', height: 7, borderRadius: 1, bgcolor: '#1e293b' }} />
                {[1, 2, 3].map((n) => (
                  <Box
                    key={n}
                    sx={{
                      height: 16,
                      borderBottom: '1px solid #e7e5e4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Box sx={{ width: '70%', height: 4, borderRadius: 1, bgcolor: '#cbd5e1' }} />
                    <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>+</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : template === 'accordion' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {[1, 2, 3].map((n) => (
                <Box
                  key={n}
                  sx={{
                    height: 28,
                    borderRadius: 1,
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
                    bgcolor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1
                  }}
                >
                  <Box sx={{ width: '55%', height: 6, borderRadius: 1, bgcolor: '#cbd5e1' }} />
                  <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>∨</Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, borderBottom: '1px solid #e5e7eb', pb: 0.5 }}>
                {['A', 'B', 'C'].map((label, idx) => (
                  <Typography
                    key={label}
                    sx={{
                      fontSize: 10,
                      fontWeight: idx === 0 ? 700 : 500,
                      color: idx === 0 ? 'primary.main' : '#94a3b8',
                      borderBottom: idx === 0 ? '2px solid' : '2px solid transparent',
                      borderColor: idx === 0 ? 'primary.main' : 'transparent',
                      pb: 0.25
                    }}
                  >
                    Sekme {label}
                  </Typography>
                ))}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {[1, 2].map((n) => (
                  <Box
                    key={n}
                    sx={{
                      height: 28,
                      borderRadius: 1,
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
                      bgcolor: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1
                    }}
                  >
                    <Box sx={{ width: '60%', height: 6, borderRadius: 1, bgcolor: '#cbd5e1' }} />
                    <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>∨</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      );
    })}
  </Box>
);

export default FaqSectionTemplatePreview;
